import { format, addDays, startOfWeek, isToday } from 'date-fns'
import { sk } from 'date-fns/locale'
import { useState, useMemo, useEffect, useCallback } from 'react'

import {
	WORK_WEEK_DAYS,
	CALENDAR_DEFAULTS
} from '@/app/[locale]/order/_utils/calendar.utils'

import type { CalendarTimeslot, DateSlot } from './a-product-template-calendar-types'

/**
 * Headless state for a Monday–Friday work-week calendar: navigation, day selection, and slot helpers.
 *
 * @param slots - Flat `DateSlot[]` keyed by `date` (`yyyy-MM-dd`); defaults to an empty list.
 * @param startHour - First hour row in the desktop timeline (inclusive).
 * @param endHour - Hour after the last row (exclusive), i.e. timeline spans `[startHour, endHour)`.
 * @param initialDateFrom - Optional `yyyy-MM-dd` used to align the initial week start via `startOfWeek`.
 * @param initialDateTo - Reserved for API symmetry; week length is fixed by {@link WORK_WEEK_DAYS}.
 * @param onWeekChange - Called with `{ dateFrom, dateTo }` as `yyyy-MM-dd` when the user changes week.
 * @returns Week model, selected day, derived slot lists, and helpers (`convertSlotToTimeslot`, etc.).
 *
 * @remarks `initialDateTo` is accepted for call-site compatibility but does not change week bounds here.
 */
export function useCalendarCore(
	slots: DateSlot[] = [],
	startHour: number = CALENDAR_DEFAULTS.START_HOUR,
	endHour: number = CALENDAR_DEFAULTS.END_HOUR,
	initialDateFrom?: string,
	initialDateTo?: string,
	onWeekChange?: (dateFrom: string, dateTo: string) => void
) {
	const [currentWeekStart, setCurrentWeekStart] = useState(() => {
		if (initialDateFrom) {
			return new Date(initialDateFrom)
		}
		return startOfWeek(new Date(), { weekStartsOn: CALENDAR_DEFAULTS.WEEK_STARTS_ON })
	})
	const [selectedDayIndex, setSelectedDayIndex] = useState(0)

	useEffect(() => {
		if (initialDateFrom) {
			const newWeekStart = startOfWeek(new Date(initialDateFrom), {
				weekStartsOn: CALENDAR_DEFAULTS.WEEK_STARTS_ON
			})
			setCurrentWeekStart(newWeekStart)
		}
	}, [initialDateFrom])

	const weekDays = useMemo(() => {
		return Array.from({ length: WORK_WEEK_DAYS }, (_, index) => {
			const date = addDays(currentWeekStart, index)
			return {
				date,
				dayName: format(date, 'EEEE', { locale: sk }),
				dayShort: format(date, 'EEE', { locale: sk }),
				dayNumber: format(date, 'd'),
				fullDate: format(date, 'yyyy-MM-dd'),
				isToday: isToday(date)
			}
		})
	}, [currentWeekStart])

	const hours = useMemo(() => {
		return Array.from({ length: endHour - startHour }, (_, i) => {
			const hour = startHour + i
			return `${hour.toString().padStart(2, '0')}:00`
		})
	}, [startHour, endHour])

	const getDaySlots = useCallback(
		(dateStr: string) => {
			return slots.filter(slot => slot.date === dateStr)
		},
		[slots]
	)

	const convertSlotToTimeslot = (slot: DateSlot): CalendarTimeslot => {
		if (slot.startTimeISO && slot.endTimeISO) {
			return {
				id: slot.id || Date.now(),
				startTime: slot.startTimeISO,
				endTime: slot.endTimeISO,
				free: slot.free ?? true
			}
		}

		const startDateTime = new Date(`${slot.date}T${slot.startTime}`)
		const endDateTime = new Date(startDateTime.getTime() + slot.duration * 60000)

		return {
			id: slot.id || Date.now(),
			startTime: startDateTime.toISOString(),
			endTime: endDateTime.toISOString(),
			free: slot.free ?? true
		}
	}

	const timeToPercentage = (time: string) => {
		const [hours, minutes] = time.split(':').map(Number)
		const totalMinutes = (hours - startHour) * 60 + minutes
		const totalDayMinutes = (endHour - startHour) * 60
		return (totalMinutes / totalDayMinutes) * 100
	}

	function getEndTime(startTime: string, duration: number) {
		const [h, m] = startTime.split(':').map(Number)
		const startTotal = h * 60 + m
		const endTotal = startTotal + duration
		const endH = Math.floor(endTotal / 60)
		const endM = endTotal % 60
		return `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`
	}

	const goToPrevWeek = useCallback(() => {
		const newWeekStart = addDays(currentWeekStart, -7)
		setCurrentWeekStart(newWeekStart)
		setSelectedDayIndex(0)

		if (onWeekChange) {
			const newDateFrom = format(newWeekStart, 'yyyy-MM-dd')
			const newDateTo = format(addDays(newWeekStart, WORK_WEEK_DAYS - 1), 'yyyy-MM-dd')
			onWeekChange(newDateFrom, newDateTo)
		}
	}, [currentWeekStart, onWeekChange])

	const goToNextWeek = useCallback(() => {
		const newWeekStart = addDays(currentWeekStart, 7)
		setCurrentWeekStart(newWeekStart)
		setSelectedDayIndex(0)

		if (onWeekChange) {
			const newDateFrom = format(newWeekStart, 'yyyy-MM-dd')
			const newDateTo = format(addDays(newWeekStart, WORK_WEEK_DAYS - 1), 'yyyy-MM-dd')
			onWeekChange(newDateFrom, newDateTo)
		}
	}, [currentWeekStart, onWeekChange])

	const weekRange = useMemo(() => {
		if (weekDays.length === 0) return ''
		return `${format(weekDays[0].date, 'd. M. yyyy')} – ${format(
			weekDays[WORK_WEEK_DAYS - 1].date,
			'd. M. yyyy'
		)}`
	}, [weekDays])

	const selectedDay = weekDays[selectedDayIndex]
	const selectedDate = selectedDay.fullDate
	const daySlots = getDaySlots(selectedDate)

	return {
		currentWeekStart,
		setCurrentWeekStart,
		selectedDayIndex,
		setSelectedDayIndex,
		weekDays,
		hours,
		getDaySlots,
		convertSlotToTimeslot,
		timeToPercentage,
		getEndTime,
		goToPrevWeek,
		goToNextWeek,
		weekRange,
		selectedDay,
		selectedDate,
		daySlots
	}
}
